import { fetchWorkEntities } from '@shared/api'
import { logger } from '@shared/lib/logger'
import { attempt } from '@shared/utils'
import { useAppData } from '@app/providers'
import { useIdentity } from '@vp/ubik-context'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Number of merged documents requested from the backend. The backend fetches
 * WES work entities and Albelli documents, merges and sorts them, and returns
 * this many; the UI reveals a subset client-side on "Show older projects".
 */
const PROJECTS_REQUEST_COUNT = 10

/**
 * Tenant sent to the projects endpoint. For now we always request the
 * production tenant, even in staging, because project data lives in PROD.
 */
const PROJECTS_TENANT = 'VISTAPRINT-PROD'

export const useProjects = () => {
  const { auth } = useIdentity()
  const { workEntitiesData, setWorkEntitiesData, concordApiServiceUrl } = useAppData()
  const [isLoading, setIsLoading] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)
  const hasFetchedRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const prefetch = useCallback(async () => {
    if (hasFetchedRef.current) return

    const token = auth?.getAuthorizationHeader?.()

    if (!token) {
      logger.warn('Projects prefetch skipped: no auth token', {
        action: 'projects_prefetch_skipped',
        component: 'useProjects',
      })
      return
    }

    hasFetchedRef.current = true
    setHasFailed(false)
    setIsLoading(true)

    const { data: projects, error: fetchError } = await attempt(
      fetchWorkEntities(concordApiServiceUrl, token, PROJECTS_TENANT, {
        numberOfDocuments: PROJECTS_REQUEST_COUNT,
      }),
    )

    if (fetchError) {
      hasFetchedRef.current = false
      if (isMountedRef.current) setHasFailed(true)
      logger.error('Projects prefetch failed', {
        action: 'projects_prefetch_failed',
        component: 'useProjects',
        error: fetchError.message,
      })
      if (isMountedRef.current) {
        setIsLoading(false)
      }
      return
    }

    if (!isMountedRef.current) return

    setWorkEntitiesData(projects)

    setIsLoading(false)
  }, [auth, setWorkEntitiesData, concordApiServiceUrl])

  return {
    isLoading,
    hasFailed,
    projects: workEntitiesData ?? [],
    prefetch,
  }
}
